/* 商品情報カラム（.product__info）をPCで追従表示する。
   説明が別セクションにあり純粋なCSS stickyでは全体を追えないため、
   スクロール方向に応じて固定位置をJSで切り替える。
   - 情報カラムがビューポートに収まる場合：上端をヘッダー下に固定
   - 収まらない場合：下スクロールで下端を追従／上スクロールで上端を追従させ、
     アコーディオンを含む下部まで必ずスクロールで到達できるようにする
   各モード内では画面上の位置が一定なので固定・フローともにガタつかない。 */
(() => {
  'use strict';

  const SELECTOR = {
    section: '.c_main-product',
    info: '.product__info',
    media: '.product__media',
    contentTabs: '.c_product-info-tabs, .product-content__tabs',
  };
  const BREAKPOINT_PC = 1000;
  const GAP_TOP = 24;
  const GAP_BOTTOM = 24;
  const EPS = 0.5;

  function getHeaderOffset() {
    const root = getComputedStyle(document.documentElement);
    const px = (name) => parseFloat(root.getPropertyValue(name)) || 0;

    return (
      px('--header-height') * px('--enable-sticky-header') +
      px('--announcement-bar-height') * px('--enable-sticky-announcement-bar') +
      GAP_TOP
    );
  }

  function createStickyInfo(section) {
    const info = section.querySelector(SELECTOR.info);
    if (!info) return;

    const mediaQuery = window.matchMedia(`(min-width: ${BREAKPOINT_PC}px)`);

    let isEnabled = false;
    let headerOffset = 0;
    let naturalTop = 0;
    let infoLeft = 0;
    let infoWidth = 0;
    let infoHeight = 0;
    let topLimit = 0; /* 情報カラム上端の最小ドキュメント座標（自然位置） */
    let bottomLimit = 0; /* 情報カラム上端の最大ドキュメント座標（左カラム下端に揃う位置） */
    let heldTop = 0; /* 情報カラム上端のドキュメント座標（追従計算の基準） */
    let lastScroll = 0;
    let appliedTransform = null;
    let appliedFixedTop = null;

    function getLeftColumnBottom() {
      let bottom = 0;

      const media = section.querySelector(SELECTOR.media);
      if (media) {
        bottom = Math.max(bottom, media.getBoundingClientRect().bottom + window.scrollY);
      }

      document.querySelectorAll(SELECTOR.contentTabs).forEach((el) => {
        bottom = Math.max(bottom, el.getBoundingClientRect().bottom + window.scrollY);
      });

      return bottom;
    }

    function resetStyles() {
      info.style.position = '';
      info.style.top = '';
      info.style.left = '';
      info.style.width = '';
      info.style.transform = '';
      appliedTransform = null;
      appliedFixedTop = null;
    }

    /*  上下端ピン留め（フローから外して固定）  */
    function applyFixed(top) {
      if (appliedFixedTop === top) return;
      info.style.transform = '';
      info.style.position = 'fixed';
      info.style.top = `${top}px`;
      info.style.left = `${infoLeft}px`;
      info.style.width = `${infoWidth}px`;
      appliedFixedTop = top;
      appliedTransform = null;
    }

    /*  通常フロー（transformで位置を保持しページと一緒に動かす）  */
    function applyFlow(translateY) {
      if (appliedFixedTop === null && appliedTransform === translateY) return;
      info.style.position = '';
      info.style.top = '';
      info.style.left = '';
      info.style.width = '';
      info.style.transform = translateY ? `translateY(${translateY}px)` : '';
      appliedTransform = translateY;
      appliedFixedTop = null;
    }

    function update() {
      if (!isEnabled) return;

      const scroll = window.scrollY;
      const viewportH = window.innerHeight;

      /*  追従の余地が無ければ常に自然位置  */
      if (bottomLimit <= topLimit) {
        applyFlow(0);
        lastScroll = scroll;
        return;
      }

      const winTop = scroll + headerOffset; /* 上端ピン時の上端座標 */
      const winBottomTop = scroll + viewportH - GAP_BOTTOM - infoHeight; /* 下端ピン時の上端座標 */
      const fits = infoHeight <= viewportH - headerOffset - GAP_BOTTOM;

      if (fits) {
        heldTop = winTop;
      } else if (scroll > lastScroll) {
        /*  下スクロール：下端がビューポート下端より上がらないよう追従  */
        heldTop = Math.max(heldTop, winBottomTop);
      } else if (scroll < lastScroll) {
        /*  上スクロール：上端がビューポート上端より下がらないよう追従  */
        heldTop = Math.min(heldTop, winTop);
      }
      heldTop = Math.min(Math.max(heldTop, topLimit), bottomLimit);
      lastScroll = scroll;

      if (heldTop <= topLimit + EPS) {
        /*  自然位置（追従開始前）  */
        applyFlow(0);
      } else if (heldTop >= bottomLimit - EPS) {
        /*  左カラム下端に到達（以降はページと一緒に流れる）  */
        applyFlow(bottomLimit - naturalTop);
      } else if (Math.abs(heldTop - winTop) < EPS) {
        /*  上端ピン  */
        applyFixed(headerOffset);
      } else if (Math.abs(heldTop - winBottomTop) < EPS) {
        /*  下端ピン（アコーディオンなど下部を表示）  */
        applyFixed(viewportH - GAP_BOTTOM - infoHeight);
      } else {
        /*  方向転換の途中：現在位置で固定してページと一緒に動かす  */
        applyFlow(heldTop - naturalTop);
      }
    }

    function measure(reseed) {
      resetStyles();
      headerOffset = getHeaderOffset();

      const infoRect = info.getBoundingClientRect();
      naturalTop = infoRect.top + window.scrollY;
      infoLeft = infoRect.left;
      infoWidth = infoRect.width;
      infoHeight = info.offsetHeight;

      topLimit = naturalTop;
      bottomLimit = getLeftColumnBottom() - infoHeight;

      /*  スクロール途中で読み込む/再計測すると方向差分が無く追従が始まらないため、
          現在のスクロール量に合わせた上端ピン位置を基準にする。
          既存位置が画面外に外れている場合も同様に引き戻す。 */
      const viewportTop = window.scrollY;
      const winTop = viewportTop + headerOffset;
      const offscreen =
        heldTop + infoHeight <= winTop || heldTop >= viewportTop + window.innerHeight;
      if (reseed || offscreen) heldTop = winTop;
      heldTop = Math.min(Math.max(heldTop, topLimit), Math.max(bottomLimit, topLimit));
      lastScroll = viewportTop;

      update();
    }

    function setEnabled(enabled) {
      isEnabled = enabled;
      if (enabled) {
        measure(true);
      } else {
        resetStyles();
      }
    }

    let resizeTimer;
    function handleResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (isEnabled) measure(false);
      }, 200);
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', handleResize);
    window.addEventListener('load', () => {
      if (isEnabled) measure(false);
    });

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', (event) => setEnabled(event.matches));
    } else {
      mediaQuery.addListener((event) => setEnabled(event.matches));
    }

    if (window.ResizeObserver) {
      const observer = new ResizeObserver(() => {
        if (isEnabled) measure(false);
      });
      observer.observe(info);

      const media = section.querySelector(SELECTOR.media);
      if (media) observer.observe(media);

      document.querySelectorAll(SELECTOR.contentTabs).forEach((el) => observer.observe(el));
    }

    setEnabled(mediaQuery.matches);
  }

  function init() {
    document.querySelectorAll(SELECTOR.section).forEach(createStickyInfo);
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SELECTOR.section);
    if (section) createStickyInfo(section);
  });
})();
